import React, { useState, useRef, useEffect } from 'react';
import { EllipsisVertical, FolderOpen, SquarePen, Copy, Download, Trash2 } from 'lucide-react';
import type { Engagement } from '../types';

interface ActionsMenuProps {
  engagement: Engagement;
  onOpen: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDownload: () => void;
  onDelete: () => void;
}

export const ActionsMenu: React.FC<ActionsMenuProps> = ({
  
  onOpen,
  onEdit,
  onDuplicate,
  onDownload,
  onDelete,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpwards, setOpenUpwards] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!isOpen && buttonRef.current) {
      // Calculate available space below the button
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      
      // If less than 150px space below, open upwards
      setOpenUpwards(spaceBelow < 150);
    }
    
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="p-1.5 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded transition-colors"
      >
        <EllipsisVertical className="w-5 h-5 text-taxtrail-text dark:text-taxtrail-dark-text" />
      </button>

      {isOpen && (
        <div 
          className={`absolute right-0 w-48 glass-panel rounded-lg py-1 z-50 transition-colors duration-300 ${
            openUpwards ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
              setIsOpen(false);
            }}
            className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors flex items-center gap-2 text-taxtrail-text dark:text-taxtrail-dark-text"
          >
            <FolderOpen className="w-4 h-4" />
            Open
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
              setIsOpen(false);
            }}
            className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors flex items-center gap-2 text-taxtrail-text dark:text-taxtrail-dark-text"
          >
            <SquarePen className="w-4 h-4" />
            Edit
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
              setIsOpen(false);
            }}
            className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors flex items-center gap-2 text-taxtrail-text dark:text-taxtrail-dark-text"
          >
            <Copy className="w-4 h-4" />
            Duplicate
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDownload();
              setIsOpen(false);
            }}
            className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors flex items-center gap-2 text-taxtrail-text dark:text-taxtrail-dark-text"
          >
            <Download className="w-4 h-4" />
            Download
          </button>
          <div className="border-t border-slate-200 dark:border-neutral-700 my-1" />
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
              setIsOpen(false);
            }}
            className="w-full px-4 py-2 text-left hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-2 text-taxtrail-red dark:text-red-400"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

